// src/composables/usePlaceBet.js
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useBetSlipStore } from '@/stores/betSlipStore'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useBonusStore } from '@/stores/useBonusStore'
import { useOddsStore } from '@/stores/oddsStore'
import apiClient from '@/api/axios'

export function usePlaceBet() {
  const router = useRouter()
  const betSlipStore = useBetSlipStore()
  const authStore = useAuthStore()
  const notificationStore = useNotificationStore()
  const bonusStore = useBonusStore()
  const oddsStore = useOddsStore()

  const loading = ref(false)
  const errorMatchId = ref(null)

  /**
   * Create the payload for placing a bet
   * @param {number} amount - The bet amount
   * @param {string} bonusId - Optional bonus ID for freebet
   * @returns {Object} - The bet payload
   */
  const createPlaceBetPayload = (amount, bonusId = '') => {
    const data = betSlipStore.bets.map((bet) => {
      // Determine o_specifier based on market_name
      // Common market names: "1x2", "Over/Under", "Both Teams To Score", etc.
      let oSpecifier = bet.market_name?.toLowerCase() || ''

      // Map common market names to expected specifiers
      const specifierMap = {
        '1x2': '1x2',
        'match result': '1x2',
        'full time result': '1x2',
        'over/under': 'over/under',
        'total goals': 'over/under',
        'both teams to score': 'both teams to score',
        'gg/ng': 'both teams to score',
        'double chance': 'double chance',
        handicap: 'handicap',
        'asian handicap': 'asian handicap',
        'correct score': 'correct score',
        'half time/full time': 'half time/full time',
        'draw no bet': 'draw no bet',
      }

      // Try to find a match in the map
      for (const [key, value] of Object.entries(specifierMap)) {
        if (oSpecifier.includes(key)) {
          oSpecifier = value
          break
        }
      }

      // If no match found, use the original market_name or default to '1x2'
      if (!oSpecifier || oSpecifier === bet.market_name?.toLowerCase()) {
        oSpecifier = '1x2'
      }

      return {
        o_id: Number(bet.outcome_id),
        o_name: bet.outcome_name,
        o_specifier: oSpecifier,
        odd_type: bet.alias || '1',
        odd_value: parseFloat(bet.odds),
        parent_match_id: Number(bet.match_id),
        sport_id: Number(bet.sport_id),
        parent_market_id: Number(bet.parent_market_id),
        market_id: Number(bet.market_id),
        is_live: bet.live === 'l' ? 'l' : 'p',
      }
    })

    const payload = {
      bonus_id: bonusId || '',
      bet_amount: Number(amount),
      bet_data: data,
      bet_ref: betSlipStore.bookingCode || '',
      timestamp: Math.floor(Date.now() / 1000),
    }

    // Add shared_bet_code only when bet originated from shared source
    if (authStore.isSharedBet && authStore.acc_no) {
      payload.shared_bet_code = `${authStore.acc_no}_${Math.floor(Date.now() / 1000)}`
    }

    console.debug('📤 placeBet payload:', JSON.stringify(payload, null, 2))
    return payload
  }

  /**
   * Place a bet
   * @param {number} stakeAmount - The stake amount
   * @param {boolean} useFreebet - Whether to use a freebet
   * @param {number} freebetAmount - The freebet amount if using freebet
   * @returns {Promise<Object>} - Result object with success status
   */
  const placeBet = async (stakeAmount, useFreebet = false, freebetAmount = 0) => {
    // Reset error state
    errorMatchId.value = null

    // Pre-flight checks
    if (!betSlipStore.hasBets) {
      notificationStore.showNotification('No bets selected.', 'warning')
      return { success: false }
    }

    const MINIMUM_TOTAL_ODDS = 1.15
    if (parseFloat(betSlipStore.totalOdds) < MINIMUM_TOTAL_ODDS) {
      notificationStore.showNotification(
        `Total odds must be at least ${MINIMUM_TOTAL_ODDS.toFixed(2)}. Current total odds: ${betSlipStore.totalOdds}.`,
        'warning',
      )
      return { success: false }
    }

    if (!authStore.isAuthenticated) {
      notificationStore.showNotification('Please log in to place a bet.', 'warning')
      router.push({ name: 'login' })
      return { success: false }
    }

    // Generate booking code if not exists
    if (!betSlipStore.bookingCode) {
      betSlipStore.generateBookingCode()
    }

    loading.value = true

    try {
      // Determine amount and bonus ID
      const amount = useFreebet ? freebetAmount : stakeAmount
      let bonusId = ''

      if (useFreebet) {
        // Find the bonus with matching amount
        const bonus = bonusStore.bonuses.find(
          (b) => String(b.status) === '1' && parseFloat(b.bonus_amount) === freebetAmount,
        )
        bonusId = bonus?.ref_id || ''
      }

      // Make API call
      const resp = await apiClient.post(
        import.meta.env.VITE_API_PLACE_BET_ENDPOINT,
        createPlaceBetPayload(amount, bonusId),
      )

      const root = resp.data

      // Normalize envelope
      let statusCode, message, payload
      if (root.code === 'Success' && root.data?.code) {
        statusCode = root.data.code
        message = root.data.message
        payload = root.data.data
      } else if (root.code === 'Error' && root.data?.code) {
        statusCode = root.data.code
        message = root.data.message || root.statusDescription
        payload = root.data.data || null
      } else if (typeof root.code === 'number') {
        statusCode = root.code
        message = root.message
        payload = root.data
      } else {
        throw new Error('Unexpected placeBet response format')
      }

      // SUCCESS (200)
      if (statusCode === 200 && payload) {
        // Snapshot values BEFORE clearing bets
        const stakeUsed = useFreebet ? freebetAmount : stakeAmount
        const oddsSnapshot = Number(betSlipStore.totalOdds).toFixed(2)
        const payoutSnapshot = Number(betSlipStore.potentialWin).toFixed(2)

        authStore.setLastBetSuccess(true)
        authStore.setLastBetStake(stakeUsed)
        authStore.setLastBetOdds(oddsSnapshot)
        authStore.setLastBetPayout(payoutSnapshot)

        // Clear & refresh after snapshot
        betSlipStore.clearBets()
        betSlipStore.clearBookingCode()
        await authStore.fetchBalance()
        await bonusStore.fetchBonuses()

        // Create a detailed success message with bet information
        // Based on the API response structure you provided:
        // {
        //   "code": "Success",
        //   "statusDescription": "Request is successful",
        //   "data": {
        //     "code": 200,
        //     "message": "Live BET is being processed.",
        //     "data": {
        //       "bet_reference": "SLJ8GT3WV",
        //       "total_odd": 1.19,
        //       "possible_win": 5.95,
        //       "freebet": false,
        //       "fb_amount": 0
        //     }
        //   }
        // }
        const betReference = payload.bet_reference || payload.ref_id || 'N/A'
        const totalOdds = payload.total_odd !== undefined ? payload.total_odd : oddsSnapshot
        const possibleWin =
          payload.possible_win !== undefined ? payload.possible_win : payoutSnapshot

        const successMessage = message || 'Bet placed successfully!'
        const detailedMessage = `${successMessage} Bet Ref: ${betReference}, Total Odds: ${totalOdds}, Possible Win: ${possibleWin}`

        notificationStore.showNotification(detailedMessage, 'success')

        // Set the bet ID in the odds store for sharing
        if (betReference && betReference !== 'N/A') {
          oddsStore.setBetId(betReference)
        }

        return {
          success: true,
          message: successMessage,
          betReference,
          possibleWin: payoutSnapshot,
        }
      }

      // INSUFFICIENT FUNDS (402)
      if (statusCode === 402) {
        const required = payload?.required_amount ?? 0
        let rounded = Math.ceil(required)
        if (rounded < 10) rounded = 10

        // Show the error message from the API response
        notificationStore.showNotification(message || 'Insufficient funds', 'error')

        return {
          success: false,
          insufficientFunds: true,
          requiredAmount: rounded,
          message: message || 'Insufficient funds',
        }
      }

      // AUTH FAILED (401)
      if (statusCode === 401) {
        notificationStore.showNotification('Authentication failed. Please log in again.', 'error')
        router.push({ name: 'login' })
        return { success: false }
      }

      // BAD REQUEST / CONFLICT (400, 409)
      if (statusCode === 400 || statusCode === 409) {
        const errors = Array.isArray(payload) ? payload : []
        errors.forEach((err) => {
          errorMatchId.value = err.parent_match_id
        })
        const detailMsgs = errors.map((e) => e.msg).join(', ')
        notificationStore.showNotification(
          `${message}${detailMsgs ? `: ${detailMsgs}` : ''}`,
          'error',
        )
        return { success: false, errors }
      }

      // Fallback
      console.error('Unhandled placeBet status:', statusCode, message, payload)
      notificationStore.showNotification(message || 'Failed to place bet.', 'error')
      return { success: false, message }
    } catch (err) {
      console.error('placeBet caught error:', err)
      const msg = err.message || ''
      if (msg.includes('Authentication failed') || msg.includes('Session expired')) {
        notificationStore.showNotification('Session expired. Please log in again.', 'error')
        router.push({ name: 'login' })
        return { success: false }
      }
      notificationStore.showNotification(msg || 'Failed to place bet.', 'error')
      return { success: false, message: msg }
    } finally {
      loading.value = false
    }
  }

  /**
   * Load a betslip from a booking code
   * @param {string} code - The booking code
   * @returns {Promise<boolean>} - Success status
   */
  const loadBetslip = async (code) => {
    if (!code) {
      notificationStore.showNotification('Please enter a booking code.', 'warning')
      return false
    }

    loading.value = true
    try {
      const timestamp = Math.floor(Date.now() / 1000)
      const resp = await apiClient.get('/bet/v1/slips', {
        params: { bet_reference: code, timestamp },
      })

      let env = resp.data
      let statusCode, message, payload

      if (typeof env.code === 'number') {
        statusCode = env.code
        message = env.message
        payload = env.data
      } else if (env.code === 'Success' && env.data?.code) {
        statusCode = env.data.code
        message = env.data.message
        payload = env.data.data
      } else {
        console.warn('Unexpected loadBetslip envelope:', env)
        notificationStore.showNotification(
          env.message || env.statusDescription || 'Failed to load bet slip.',
          'error',
        )
        return false
      }

      if (statusCode !== 200) {
        notificationStore.showNotification(message || 'Failed to load bet slip.', 'error')
        return false
      }

      if (!payload || !Array.isArray(payload.slips)) {
        console.warn('Unexpected slips structure:', payload)
        notificationStore.showNotification('No slip details returned from server.', 'warning')
        return false
      }

      // Clear existing bets
      betSlipStore.clearBets()
      betSlipStore.clearBookingCode()

      // Add each slip to the betslip
      payload.slips.forEach((item) => {
        let teamInfo = {}
        try {
          teamInfo = JSON.parse(item.team_data)
        } catch {
          console.warn('Invalid team_data JSON for slip item:', item)
        }

        const oddObj = {
          outcome_id: item.selection_id,
          match_id: item.parent_match_id,
          parent_market_id: item.parent_market_id,
          market_id: item.market_id,
          outcome_name: item.pick,
          alias: item.pick,
          odds: parseFloat(item.odd_value),
          date: item.start_time,
          live: item.live_bet === '1' ? 'l' : 'p',
          is_live: item.live_bet === '1',
          stale: false,
          status_name: 'open',
          match_name:
            teamInfo.competitor1 && teamInfo.competitor2
              ? `${teamInfo.competitor1} VS ${teamInfo.competitor2}`
              : item.pick_name.split(';')[0] || '',
          market_name: teamInfo.cat || item.pick_name.split(';')[1] || '',
          competitor1: teamInfo.competitor1 || '',
          competitor2: teamInfo.competitor2 || '',
          sport_id: item.sport_id || 0,
        }

        betSlipStore.addBet(oddObj)
      })

      betSlipStore.setBookingCode(code)

      // Mark as shared bet when loading via booking code
      authStore.setIsSharedBet(true)
      authStore.setLastSharedBetCode(code)

      notificationStore.showNotification('Betslip loaded successfully!', 'success')
      return true
    } catch (err) {
      console.error('Error loading bet slip:', err)
      notificationStore.showNotification('Error loading bet slip. Please try again.', 'error')
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    errorMatchId,
    placeBet,
    loadBetslip,
  }
}
